/**
 * Voice Cache Module
 * Handles audio caching utilities for voice playback
 */

import { storageMechanism } from '../../../js/utils/storage.js';
import { mutateMessageAudioStorage } from './message-audio-keys.js';

// Audio cache prefixes
export const AUDIO_CACHE_PREFIX = 'voice_chunks_';
export const AUDIO_METADATA_PREFIX = 'voice_meta_';

/**
 * Cache base64 audio data
 * @param {string} key - Cache key
 * @param {string} base64 - Base64 encoded audio data
 */
export const cacheAudio = (key, base64) => {
    try {
        storageMechanism.setItem(key, base64);
    } catch (e) {
        // Ignore quota errors
    }
};

/**
 * Get cached base64 audio data
 * @param {string} key - Cache key
 * @returns {string|null} Cached base64 data or null
 */
export const getCachedAudio = (key) => {
    try {
        return storageMechanism.getItem(key);
    } catch (e) {
        return null;
    }
};

/**
 * Get cached audio chunks with metadata
 * @param {string} cacheKey - Cache key
 * @returns {Object|null} Object with audioBuffer and metadata, or null
 */
export const getCachedAudioChunks = (cacheKey) => {
    try {
        const chunksData = storageMechanism.getItem(AUDIO_CACHE_PREFIX + cacheKey);
        const metaData = storageMechanism.getItem(AUDIO_METADATA_PREFIX + cacheKey);
        if (chunksData && metaData) {
            return {
                audioBuffer: Uint8Array.from(atob(chunksData), c => c.charCodeAt(0)).buffer,
                metadata: JSON.parse(metaData)
            };
        }
    } catch (error) {
        console.warn('Failed to retrieve cached audio chunks:', error);
    }
    return null;
};

/**
 * Cache audio chunks with metadata
 * @param {string} cacheKey - Cache key
 * @param {ArrayBuffer} audioBuffer - Audio buffer to cache
 * @param {Array} chunkTimings - Chunk timing data
 * @param {number} separationMs - Separation in milliseconds
 */
export const cacheAudioChunks = (cacheKey, audioBuffer, chunkTimings, separationMs) => {
    try {
        const uint8Array = new Uint8Array(audioBuffer);
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
            const chunk = uint8Array.slice(i, i + chunkSize);
            binaryString += String.fromCharCode(...chunk);
        }
        const chunksData = btoa(binaryString);
        const metaData = JSON.stringify({ chunkTimings, separationMs, timestamp: Date.now() });
        storageMechanism.setItem(AUDIO_CACHE_PREFIX + cacheKey, chunksData);
        storageMechanism.setItem(AUDIO_METADATA_PREFIX + cacheKey, metaData);
    } catch (error) {
        console.warn('Failed to cache audio chunks:', error);
        try {
            storageMechanism.removeItem(AUDIO_CACHE_PREFIX + cacheKey);
            storageMechanism.removeItem(AUDIO_METADATA_PREFIX + cacheKey);
        } catch (e) { }
    }
};

const cleanupStorageByPrefix = (prefix) => {
    try {
        const keysToRemove = [];
        for (let i = 0; i < storageMechanism.length; i++) {
            const key = storageMechanism.key(i);
            if (key && key.startsWith(prefix)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => storageMechanism.removeItem(key));
    } catch (error) {
        console.warn('Failed to cleanup storage cache:', error);
    }
};

export function cleanupAllAudioCache() {
    cleanupStorageByPrefix('audio_chunks_');
    cleanupStorageByPrefix('audio_meta_');
    cleanupStorageByPrefix('voice_ready_');
}

export const clearStoredAudioForMessage = (characterId, messageKey, storage = storageMechanism) => {
    try {
        mutateMessageAudioStorage(storage, {
            characterId,
            messageKey,
            nextMessageKey: null
        });
    } catch (error) {
        console.warn('Failed to clear message audio cache:', error);
    }
};

export const rekeyStoredAudioForMessage = (characterId, previousMessageKey, nextMessageKey, storage = storageMechanism) => {
    try {
        if (previousMessageKey === nextMessageKey) return;
        mutateMessageAudioStorage(storage, {
            characterId,
            messageKey: previousMessageKey,
            nextMessageKey
        });
    } catch (error) {
        console.warn('Failed to rekey message audio cache:', error);
    }
};
